import React, { Suspense } from 'react';
import { Box, CircularProgress } from '@mui/material';
import { isChunkLoadError } from '../utils/isChunkLoadError';
import { isBrowserOnline } from '../../../hooks/useOnlineStatus';
import { markChunkRecoveryReload } from '../utils/chunkRecovery';
import { NetworkErrorState } from './NetworkErrorState';
import {
  OFFLINE_USER_MESSAGE,
  OFFLINE_USER_TITLE,
} from '../utils/errorUtils';

export const CHUNK_OFFLINE_TITLE = OFFLINE_USER_TITLE;

const CHUNK_OFFLINE_BODY_OFFLINE =
  'Verifique sua internet. Ao reconectar, esta página será carregada automaticamente.';

const CHUNK_OFFLINE_BODY_ONLINE =
  'Não foi possível carregar os recursos desta tela. Tente novamente.';

interface ChunkLoadErrorBoundaryProps {
  children: React.ReactNode;
  suspenseFallback?: React.ReactNode;
}

interface ChunkLoadErrorBoundaryState {
  hasError: boolean;
  retryKey: number;
  isRecovering: boolean;
  nonChunkError: unknown;
}

/**
 * Catches failed dynamic imports (offline, stale deploy, missing chunk).
 * Recovery reloads the page (webpack clears rejected lazy promises) but skips
 * the branded boot splash via sessionStorage (see chunkRecovery.ts).
 */
export class ChunkLoadErrorBoundary extends React.Component<
  ChunkLoadErrorBoundaryProps,
  ChunkLoadErrorBoundaryState
> {
  private onlineListenerBound = false;

  state: ChunkLoadErrorBoundaryState = {
    hasError: false,
    retryKey: 0,
    isRecovering: false,
    nonChunkError: null,
  };

  static getDerivedStateFromError(error: unknown): Partial<ChunkLoadErrorBoundaryState> | null {
    if (isChunkLoadError(error)) {
      return { hasError: true, nonChunkError: null, isRecovering: false };
    }
    return { hasError: false, nonChunkError: error };
  }

  componentDidCatch(error: unknown): void {
    if (!isChunkLoadError(error)) {
      return;
    }
    this.bindOnlineRetry();
  }

  componentWillUnmount(): void {
    window.removeEventListener('online', this.handleOnlineRetry);
  }

  bindOnlineRetry = (): void => {
    if (this.onlineListenerBound) return;
    this.onlineListenerBound = true;
    window.addEventListener('online', this.handleOnlineRetry);
  };

  handleOnlineRetry = (): void => {
    if (!this.state.hasError || !isBrowserOnline()) return;
    this.reloadApp();
  };

  handleManualRetry = (): void => {
    this.reloadApp();
  };

  reloadApp = (): void => {
    if (!isBrowserOnline()) {
      return;
    }
    this.setState({ isRecovering: true });
    markChunkRecoveryReload();
    window.location.reload();
  };

  render(): React.ReactNode {
    if (this.state.nonChunkError) {
      throw this.state.nonChunkError;
    }

    if (this.state.hasError) {
      const isOffline = !isBrowserOnline();
      return (
        <NetworkErrorState
          title={CHUNK_OFFLINE_TITLE}
          message={isOffline ? CHUNK_OFFLINE_BODY_OFFLINE : CHUNK_OFFLINE_BODY_ONLINE}
          onRetry={this.handleManualRetry}
          retryDisabled={isOffline}
          reconnecting={this.state.isRecovering}
          minHeight="calc(100vh - 120px)"
        />
      );
    }

    const fallback = this.props.suspenseFallback ?? (
      <Box data-splash-exclude sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress size={32} aria-valuenow={0} aria-valuemin={0} aria-valuemax={100} />
      </Box>
    );

    return (
      <Suspense key={this.state.retryKey} fallback={fallback}>
        {this.props.children}
      </Suspense>
    );
  }
}
